import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function ChangePasswordCard() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k) => () => setShow({ ...show, [k]: !show[k] });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (form.next !== form.confirm) { toast.error("Passwords don't match"); return; }
    if (form.next === form.current) { toast.error("New password must differ from current"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      setForm({ current: "", next: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    }
    setSaving(false);
  };

  return (
    <form onSubmit={submit} className="card-surface p-8 space-y-5" data-testid="change-password-card">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 border border-[#38BDF8]/50 text-[#38BDF8] grid place-items-center">
          <Lock size={16} />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Security</div>
          <h3 className="font-display text-2xl font-black uppercase tracking-tighter mt-0.5">Change password</h3>
        </div>
      </div>
      <p className="text-xs text-[#A3A3A3]">
        Use a unique, strong password. We'll email you a confirmation once it's updated.
      </p>

      <PwField label="Current password" name="current" value={form.current} onChange={set("current")} show={show.current} onToggle={toggle("current")} testId="cp-current" />
      <PwField label="New password" name="next" value={form.next} onChange={set("next")} show={show.next} onToggle={toggle("next")} minLength={6} testId="cp-new" />
      <PwField label="Confirm new password" name="confirm" value={form.confirm} onChange={set("confirm")} show={show.confirm} onToggle={toggle("confirm")} minLength={6} testId="cp-confirm" />

      <button
        disabled={saving || !form.current || !form.next || !form.confirm}
        className="btn-primary py-3 px-6 text-xs inline-flex items-center gap-2"
        data-testid="cp-submit"
      >
        <Lock size={14} /> {saving ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}

function PwField({ label, value, onChange, show, onToggle, minLength, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <div className="flex items-center gap-2 border-b border-white/20 focus-within:border-[#38BDF8]">
        <input
          type={show ? "text" : "password"}
          value={value} onChange={onChange} required minLength={minLength}
          autoComplete="new-password"
          className="flex-1 bg-transparent outline-none py-2 text-sm"
          data-testid={testId}
        />
        <button type="button" onClick={onToggle} className="text-[#A3A3A3] hover:text-white p-1" tabIndex={-1} data-testid={`${testId}-toggle`}>
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}
